import React, { useState, useEffect, useMemo } from 'react';
import { supabase } from '../../../lib/supabase';
import { Layers, Plus, Trash2, Edit2, Image as ImageIcon, Save, X, Eye, ArrowLeft } from 'lucide-react';
import { LoadingSpinner } from '../../LoadingSpinner';
import { useUserAccess } from '../../../context/UserAccessContext';
import { useAuth } from '../../../hooks/useAuth';
import { Link, useNavigate } from 'react-router-dom';
import { DeleteConfirmationModal } from '../../DeleteConfirmationModal';

interface Campaign {
  id: string;
  title: string;
  description: string;
  category: string;
  width: number;
  height: number;
  frame_url: string;
  thumbnail_url?: string;
  supporters_count: number;
  is_active: boolean;
  start_date: string | null;
  end_date: string | null;
}

export const PortalGuruKelolaBingkai: React.FC = () => {
  const { authUser } = useAuth();
  const { isSuperAdmin, checkUserAccess, loading: accessLoading } = useUserAccess();
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(true);

  const canManageBingkai = useMemo(() => {
    if (isSuperAdmin) return true;
    if (!authUser?.email) return false;
    return checkUserAccess(authUser.email, 'pg-mias-media');
  }, [isSuperAdmin, authUser, checkUserAccess]);

  // Modal State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  // Form State
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: '',
    width: 1080,
    height: 1080,
    is_active: true,
    start_date: '',
    end_date: ''
  });
  const [file, setFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string>('');

  const [thumbnailFile, setThumbnailFile] = useState<File | null>(null);
  const [thumbnailPreviewUrl, setThumbnailPreviewUrl] = useState<string>('');

  useEffect(() => {
    if (accessLoading) return;
    if (canManageBingkai) {
      fetchCampaigns();
    } else {
      setLoading(false);
    }
  }, [canManageBingkai, accessLoading]);

  const fetchCampaigns = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('bingkai_campaigns')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setCampaigns(data || []);
    } catch (err) {
      console.error('Error fetching campaigns:', err);
      alert('Gagal mengambil data kampanye.');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (campaign?: Campaign) => {
    if (campaign) {
      setEditingId(campaign.id);
      setFormData({
        title: campaign.title,
        description: campaign.description || '',
        category: campaign.category || '',
        width: campaign.width,
        height: campaign.height,
        is_active: campaign.is_active,
        start_date: campaign.start_date || '',
        end_date: campaign.end_date || ''
      });
      setPreviewUrl(campaign.frame_url);
      setThumbnailPreviewUrl(campaign.thumbnail_url || '');
    } else {
      setEditingId(null);
      setFormData({
        title: '',
        description: '',
        category: '',
        width: 1080,
        height: 1080,
        is_active: true,
        start_date: '',
        end_date: ''
      });
      setPreviewUrl('');
      setThumbnailPreviewUrl('');
    }
    setFile(null);
    setThumbnailFile(null);
    setIsModalOpen(true);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const selectedFile = e.target.files[0];
      setFile(selectedFile);
      setPreviewUrl(URL.createObjectURL(selectedFile));

      // Auto-detect resolution
      const img = new Image();
      img.onload = () => {
        setFormData(prev => ({ ...prev, width: img.width, height: img.height }));
      };
      img.src = URL.createObjectURL(selectedFile);
    }
  };

  const handleThumbnailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const selectedFile = e.target.files[0];
      setThumbnailFile(selectedFile);
      setThumbnailPreviewUrl(URL.createObjectURL(selectedFile));
    }
  };

  const uploadImage = async (fileToUpload: File): Promise<string> => {
    const fileExt = fileToUpload.name.split('.').pop();
    const fileName = `${Math.random()}.${fileExt}`;
    const filePath = `frames/${fileName}`;

    const { error: uploadError } = await supabase.storage
      .from('bingkai_frames')
      .upload(filePath, fileToUpload);

    if (uploadError) throw uploadError;

    const { data } = supabase.storage
      .from('bingkai_frames')
      .getPublicUrl(filePath);

    return data.publicUrl;
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      let finalFrameUrl = previewUrl;
      let finalThumbnailUrl = thumbnailPreviewUrl;

      if (file) {
        finalFrameUrl = await uploadImage(file);
      }

      if (thumbnailFile) {
        finalThumbnailUrl = await uploadImage(thumbnailFile);
      }

      if (!finalFrameUrl) {
        throw new Error("Gambar bingkai wajib diunggah.");
      }

      const payload = {
        title: formData.title,
        description: formData.description,
        category: formData.category,
        width: formData.width,
        height: formData.height,
        frame_url: finalFrameUrl,
        thumbnail_url: finalThumbnailUrl || null,
        is_active: formData.is_active,
        start_date: formData.start_date || null,
        end_date: formData.end_date || null,
      };

      if (editingId) {
        const { error } = await supabase
          .from('bingkai_campaigns')
          .update(payload)
          .eq('id', editingId);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('bingkai_campaigns')
          .insert([payload]);
        if (error) throw error;
      }

      setIsModalOpen(false);
      fetchCampaigns();
    } catch (error: any) {
      console.error("Error saving campaign:", error);
      alert(`Gagal menyimpan kampanye: ${error.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = (id: string) => {
    setDeleteId(id);
  };

  const confirmDelete = async () => {
    if (!deleteId) return;
    try {
      const { error } = await supabase.from('bingkai_campaigns').delete().eq('id', deleteId);
      if (error) throw error;
      fetchCampaigns();
    } catch (err: any) {
        alert("Gagal menghapus: " + err.message);
    } finally {
        setDeleteId(null);
    }
  };

  if (!accessLoading && !canManageBingkai) {
    return (
      <div className="p-8 text-center bg-red-50 text-red-600 rounded-lg m-6">
        <h2 className="text-xl font-bold mb-2">Akses Ditolak</h2>
        <p>Anda tidak memiliki izin untuk mengelola BingkaiMias.</p>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 min-h-screen p-4 sm:p-6 lg:p-8">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/portal-guru')}
              className="p-2 text-gray-600 hover:bg-gray-200 rounded-full transition-colors flex-shrink-0"
              title="Kembali ke Dasbor"
            >
              <ArrowLeft className="w-6 h-6" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center">
                <Layers className="mr-3 w-6 h-6 text-blue-600" />
                Kelola Kampanye BingkaiMias
              </h1>
              <p className="text-sm text-gray-500 mt-1">
                Tambahkan dan kelola bingkai/Twibbon untuk halaman publik.
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <Link to="/bingkaimias" target="_blank" className="flex items-center px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50">
                <Eye className="w-4 h-4 mr-2" />
                Lihat Web Publik
            </Link>
            <button
              onClick={() => handleOpenModal()}
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700"
            >
              <Plus className="w-4 h-4 mr-2" />
              Tambah Kampanye
            </button>
          </div>
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex justify-center items-center h-64"><LoadingSpinner /></div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {campaigns.map(camp => (
              <div key={camp.id} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden flex flex-col">
                <div className="h-48 bg-slate-100 relative flex items-center justify-center p-4 overflow-hidden">
                    <img src={camp.thumbnail_url || camp.frame_url} alt="" className="absolute inset-0 w-full h-full object-cover blur-2xl scale-125 opacity-40" />
                    <div className="absolute inset-0 bg-gradient-to-b from-white/10 via-transparent to-white/30 z-0" />

                    <img src={camp.thumbnail_url || camp.frame_url} alt={camp.title} className="relative z-10 max-h-full object-contain drop-shadow-md" />

                    {!camp.is_active && (
                        <div className="absolute top-2 right-2 bg-red-100 text-red-700 px-2 py-1 rounded text-xs font-bold z-20 shadow-sm">Tidak Aktif</div>
                    )}
                </div>
                <div className="p-4 flex-grow flex flex-col">
                  <div className="flex justify-between items-start mb-2">
                    <h3 className="font-bold text-gray-900 line-clamp-2">{camp.title}</h3>
                    <span className="bg-blue-50 text-blue-700 text-xs px-2 py-1 rounded whitespace-nowrap ml-2">{camp.category}</span>
                  </div>
                  <p className="text-sm text-gray-500 line-clamp-2 mb-4 flex-grow">{camp.description}</p>

                  <div className="flex justify-between items-center text-xs text-gray-500 mb-4 bg-gray-50 p-2 rounded">
                    <div>
                        <span className="block font-semibold">Resolusi</span>
                        {camp.width} x {camp.height}
                    </div>
                    <div className="text-right">
                        <span className="block font-semibold">Partisipan</span>
                        {camp.supporters_count}
                    </div>
                  </div>

                  <div className="flex justify-end gap-2 border-t pt-4">
                    <button onClick={() => handleOpenModal(camp)} className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors">
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button onClick={() => handleDelete(camp.id)} className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}

            {campaigns.length === 0 && (
                <div className="col-span-full bg-white p-12 text-center rounded-xl border border-dashed border-gray-300">
                    <Layers className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <h3 className="text-lg font-medium text-gray-900">Belum ada kampanye</h3>
                    <p className="text-gray-500 mt-1">Buat kampanye BingkaiMias pertama Anda.</p>
                </div>
            )}
          </div>
        )}
      </div>

      {/* Form Modal (Enhanced UI) */}
      {isModalOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-gray-900/60 backdrop-blur-sm p-4 sm:p-6 transition-all">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden animate-in zoom-in-95 duration-200">
            {/* Modal Header */}
            <div className="px-6 py-5 border-b border-gray-100 flex justify-between items-center bg-white sticky top-0 z-10">
              <div className="flex items-center gap-3">
                <div className="bg-blue-50 p-2 rounded-lg">
                    <Layers className="w-5 h-5 text-blue-600" />
                </div>
                <h2 className="text-xl font-bold text-gray-900">
                    {editingId ? 'Edit Kampanye' : 'Tambah Kampanye Baru'}
                </h2>
              </div>
              <button onClick={() => setIsModalOpen(false)} className="text-gray-400 hover:text-gray-700 hover:bg-gray-100 p-2 rounded-full transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSave} className="overflow-y-auto flex-grow bg-gray-50/50">
              <div className="p-6">
                  <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">

                    {/* Left Column: Form Details */}
                    <div className="lg:col-span-7 space-y-6">

                        {/* Section: Informasi Dasar */}
                        <div className="bg-white p-5 rounded-xl border border-gray-100 shadow-sm space-y-4">
                            <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide border-b border-gray-100 pb-2 mb-4">Informasi Dasar</h3>
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Judul Kampanye <span className="text-red-500">*</span></label>
                                <input required type="text" placeholder="Masukkan judul yang menarik" value={formData.title} onChange={e => setFormData({...formData, title: e.target.value})} className="w-full rounded-lg border-gray-200 bg-gray-50 focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all text-sm px-4 py-2.5" />
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Kategori <span className="text-red-500">*</span></label>
                                <input required type="text" placeholder="Misal: Edukasi, Lingkungan, Event" value={formData.category} onChange={e => setFormData({...formData, category: e.target.value})} className="w-full rounded-lg border-gray-200 bg-gray-50 focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all text-sm px-4 py-2.5" />
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Deskripsi Lengkap</label>
                                <textarea rows={4} placeholder="Jelaskan tujuan dan detail kampanye ini..." value={formData.description} onChange={e => setFormData({...formData, description: e.target.value})} className="w-full rounded-lg border-gray-200 bg-gray-50 focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all text-sm px-4 py-2.5 resize-none" />
                            </div>
                        </div>

                        {/* Section: Pengaturan Waktu & Status */}
                        <div className="bg-white p-5 rounded-xl border border-gray-100 shadow-sm space-y-4">
                            <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide border-b border-gray-100 pb-2 mb-4">Pengaturan</h3>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">Tanggal Mulai</label>
                                    <input type="date" value={formData.start_date} onChange={e => setFormData({...formData, start_date: e.target.value})} className="w-full rounded-lg border-gray-200 bg-gray-50 focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all text-sm px-4 py-2.5" />
                                </div>
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">Tanggal Selesai</label>
                                    <input type="date" value={formData.end_date} onChange={e => setFormData({...formData, end_date: e.target.value})} className="w-full rounded-lg border-gray-200 bg-gray-50 focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all text-sm px-4 py-2.5" />
                                </div>
                            </div>

                            <div className="mt-6 pt-4 border-t border-gray-100">
                                <label className="flex items-center justify-between cursor-pointer group">
                                    <div>
                                        <span className="block text-sm font-semibold text-gray-900">Status Kampanye Aktif</span>
                                        <span className="block text-xs text-gray-500 mt-0.5">Tampilkan kampanye ini di halaman publik agar dapat diakses.</span>
                                    </div>
                                    <div className="relative inline-flex items-center">
                                        <input type="checkbox" checked={formData.is_active} onChange={e => setFormData({...formData, is_active: e.target.checked})} className="sr-only peer" />
                                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-100 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                                    </div>
                                </label>
                            </div>
                        </div>

                    </div>

                    {/* Right Column: Image Upload & Preview */}
                    <div className="lg:col-span-5 space-y-6">

                        <div className="bg-white p-5 rounded-xl border border-gray-100 shadow-sm space-y-4 sticky top-6">
                            <div className="flex justify-between items-center border-b border-gray-100 pb-2 mb-4">
                                <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide">Gambar Bingkai</h3>
                                <span className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded font-medium">Wajib PNG</span>
                            </div>

                            <div className="group relative w-full h-64 border-2 border-dashed border-gray-200 rounded-xl bg-gray-50 hover:bg-gray-100 hover:border-blue-400 transition-all overflow-hidden flex flex-col items-center justify-center cursor-pointer"
                                 onClick={() => document.getElementById('frame-upload-input')?.click()}
                            >
                                {previewUrl ? (
                                    <div className="absolute inset-0 w-full h-full checkerboard" style={{
                                        backgroundImage: `linear-gradient(45deg, #f1f5f9 25%, transparent 25%), linear-gradient(-45deg, #f1f5f9 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #f1f5f9 75%), linear-gradient(-45deg, transparent 75%, #f1f5f9 75%)`,
                                        backgroundSize: '20px 20px',
                                        backgroundPosition: '0 0, 0 10px, 10px -10px, -10px 0px'
                                    }}>
                                        <img src={previewUrl} alt="Preview Bingkai" className="w-full h-full object-contain p-2 drop-shadow-sm" />
                                        {/* Hover Overlay */}
                                        <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center text-white backdrop-blur-sm">
                                            <Edit2 className="w-8 h-8 mb-2" />
                                            <span className="font-semibold text-sm">Ganti Gambar Bingkai</span>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="text-center p-6 space-y-3 pointer-events-none">
                                        <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto shadow-sm border border-gray-100 group-hover:scale-110 transition-transform">
                                            <ImageIcon className="w-8 h-8 text-gray-400 group-hover:text-blue-500 transition-colors" />
                                        </div>
                                        <div>
                                            <p className="text-sm font-semibold text-gray-700">Klik untuk mengunggah bingkai</p>
                                            <p className="text-xs text-gray-500 mt-1">Format harus PNG dengan area transparan (bolong) di tengah.</p>
                                        </div>
                                    </div>
                                )}
                                <input id="frame-upload-input" type="file" required={!editingId} accept="image/png" className="hidden" onChange={handleFileChange} />
                            </div>

                            <div className="bg-gray-50 rounded-lg p-4 border border-gray-100">
                                <h4 className="text-xs font-bold text-gray-500 uppercase mb-3">Deteksi Resolusi Gambar</h4>
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <label className="block text-xs font-medium text-gray-500 mb-1">Lebar (px)</label>
                                        <div className="bg-white border border-gray-200 rounded-md px-3 py-2 text-sm font-semibold text-gray-700 text-center">
                                            {formData.width}
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-xs font-medium text-gray-500 mb-1">Tinggi (px)</label>
                                        <div className="bg-white border border-gray-200 rounded-md px-3 py-2 text-sm font-semibold text-gray-700 text-center">
                                            {formData.height}
                                        </div>
                                    </div>
                                </div>
                                <p className="text-[10px] text-gray-400 mt-2 text-center">*Otomatis terisi saat gambar frame dipilih.</p>
                            </div>

                        </div>

                        {/* Section: Thumbnail / Preview */}
                        <div className="bg-white p-5 rounded-xl border border-gray-100 shadow-sm space-y-4">
                            <div className="flex justify-between items-center border-b border-gray-100 pb-2 mb-4">
                                <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide">Gambar Thumbnail (Opsional)</h3>
                                <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded font-medium">JPG/PNG</span>
                            </div>
                            <p className="text-xs text-gray-500 mb-2">Unggah contoh foto yang sudah dipasang bingkai untuk ditampilkan sebagai pratinjau di halaman depan publik.</p>

                            <div className="group relative w-full h-48 border-2 border-dashed border-gray-200 rounded-xl bg-gray-50 hover:bg-gray-100 hover:border-blue-400 transition-all overflow-hidden flex flex-col items-center justify-center cursor-pointer"
                                 onClick={() => document.getElementById('thumbnail-upload-input')?.click()}
                            >
                                {thumbnailPreviewUrl ? (
                                    <div className="absolute inset-0 w-full h-full">
                                        <img src={thumbnailPreviewUrl} alt="Preview Thumbnail" className="w-full h-full object-cover" />
                                        <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center text-white backdrop-blur-sm">
                                            <Edit2 className="w-8 h-8 mb-2" />
                                            <span className="font-semibold text-sm">Ganti Thumbnail</span>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="text-center p-6 space-y-3 pointer-events-none">
                                        <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center mx-auto shadow-sm border border-gray-100 group-hover:scale-110 transition-transform">
                                            <ImageIcon className="w-6 h-6 text-gray-400 group-hover:text-blue-500 transition-colors" />
                                        </div>
                                        <div>
                                            <p className="text-sm font-semibold text-gray-700">Klik untuk mengunggah thumbnail</p>
                                        </div>
                                    </div>
                                )}
                                <input id="thumbnail-upload-input" type="file" accept="image/png, image/jpeg, image/webp" className="hidden" onChange={handleThumbnailChange} />
                            </div>
                        </div>

                    </div>

                  </div>
              </div>

              {/* Modal Footer */}
              <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-3 bg-white sticky bottom-0 z-10">
                <button type="button" onClick={() => setIsModalOpen(false)} className="px-5 py-2.5 bg-white border border-gray-300 rounded-xl text-sm font-semibold text-gray-700 hover:bg-gray-50 focus:ring-4 focus:ring-gray-100 transition-all">
                  Batalkan
                </button>
                <button type="submit" disabled={isSaving} className="flex items-center px-6 py-2.5 bg-blue-600 border border-transparent rounded-xl text-sm font-semibold text-white hover:bg-blue-700 focus:ring-4 focus:ring-blue-200 disabled:opacity-70 disabled:cursor-not-allowed transition-all shadow-sm">
                  {isSaving ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Menyimpan...
                    </>
                  ) : (
                    <>
                        <Save className="w-4 h-4 mr-2" />
                        Simpan Kampanye
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={!!deleteId}
        onClose={() => setDeleteId(null)}
        onConfirm={confirmDelete}
        title="Hapus Kampanye"
        message="Apakah Anda yakin ingin menghapus kampanye bingkai ini? Data tidak dapat dikembalikan."
      />
    </div>
  );
};
